if (!customElements.get('tdm-sticky-buy-bar')) {
  customElements.define(
    'tdm-sticky-buy-bar',
    class TdmStickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.mainSubmitButton = document.querySelector('.product-form__submit:not(.tdm-sticky-bar__submit)');
        this.form = this.mainSubmitButton ? this.mainSubmitButton.closest('form') : null;
        if (!this.form) return;

        this.variantIdInput = this.form.querySelector('.product-variant-id');
        this.sectionId = document.querySelector('product-info')?.dataset.section;
        this.defaultImage = this.dataset.defaultImage;
        this.variants = JSON.parse(this.querySelector('[data-tdm-sticky-variants]').textContent);

        this.image = this.querySelector('.tdm-sticky-bar__image');
        this.select = this.querySelector('.tdm-sticky-bar__select');
        this.submitBtn = this.querySelector('.tdm-sticky-bar__submit');
        this.submitLabel = this.querySelector('.tdm-sticky-bar__submit-label');
        this.submitPrice = this.querySelector('.tdm-sticky-bar__submit-price');
        this.qtyInput = this.querySelector('.tdm-sticky-bar__qty-input');
        this.qtyMinus = this.querySelector('[data-qty-minus]');
        this.qtyPlus = this.querySelector('[data-qty-plus]');

        this.mainQuantityInput = this.getOrCreateMainQuantityInput();

        this.bindQuantity();
        this.bindVariantSelect();
        this.bindSubmit();
        this.bindVisibility();
        this.bindRemoteVariantChange();
      }

      getOrCreateMainQuantityInput() {
        let input = this.form.querySelector('input[name="quantity"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'quantity';
          input.value = '1';
          this.form.appendChild(input);
        }
        return input;
      }

      bindQuantity() {
        const clamp = (value) => Math.max(parseInt(this.qtyInput.min || '1', 10), parseInt(value, 10) || 1);

        const setQuantity = (value, { silent } = {}) => {
          const next = clamp(value);
          this.qtyInput.value = next;
          if (this.mainQuantityInput.value != next) {
            this.mainQuantityInput.value = next;
            if (!silent) this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };

        this.qtyMinus.addEventListener('click', () => setQuantity(parseInt(this.qtyInput.value, 10) - 1));
        this.qtyPlus.addEventListener('click', () => setQuantity(parseInt(this.qtyInput.value, 10) + 1));
        this.qtyInput.addEventListener('change', () => setQuantity(this.qtyInput.value));

        // Reflect changes made on the real page's own quantity input, if present.
        this.mainQuantityInput.addEventListener('change', () => {
          if (this.mainQuantityInput.value != this.qtyInput.value) {
            setQuantity(this.mainQuantityInput.value, { silent: true });
          }
        });

        setQuantity(this.mainQuantityInput.value || 1, { silent: true });
      }

      // The bar drives its own state directly (price, image, hidden variant
      // id used on submit) instead of puppeteering the real, on-page picker.
      // Simpler and avoids depending on Dawn's fetch/focus/gallery pipeline.
      bindVariantSelect() {
        if (!this.select) return;

        this.select.addEventListener('change', () => {
          this.applyVariant(this.select.value);
        });
      }

      applyVariant(variantId) {
        const variant = this.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;

        if (this.variantIdInput) this.variantIdInput.value = variant.id;

        const selectedOption = this.select && this.select.querySelector(`option[value="${variantId}"]`);
        if (this.submitPrice && selectedOption) {
          this.submitPrice.innerHTML = `<span class="separator-price"> - </span>${selectedOption.dataset.money}`;
        }

        if (this.image) {
          if (variant.featured_image && variant.featured_image.src) {
            this.image.src = this.resizeImageUrl(variant.featured_image.src);
          } else if (this.defaultImage) {
            this.image.src = this.defaultImage;
          }
        }

        this.updateAvailability(variant);
      }

      // Reflects a variant change made on the real, on-page picker back onto
      // the sticky bar (image, select value, price, availability).
      bindRemoteVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (this.sectionId && event.data.sectionId !== this.sectionId) return;

          const { html } = event.data;
          const variantIdInput = html.querySelector('.product-variant-id');
          const priceSource = this.sectionId ? html.getElementById(`ButtonPrice-${this.sectionId}`) : null;
          if (!variantIdInput) return;

          const variantId = variantIdInput.value;
          const variant = this.variants.find((v) => String(v.id) === String(variantId));
          if (!variant) return;

          if (this.select && this.select.value !== String(variantId)) {
            this.select.value = variantId;
          }

          if (this.image) {
            if (variant.featured_image && variant.featured_image.src) {
              this.image.src = this.resizeImageUrl(variant.featured_image.src);
            } else if (this.defaultImage) {
              this.image.src = this.defaultImage;
            }
          }

          if (priceSource && this.submitPrice) {
            this.submitPrice.innerHTML = priceSource.innerHTML;
          }

          this.updateAvailability(variant);
        });
      }

      resizeImageUrl(src) {
        return src.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_100x$1$2');
      }

      updateAvailability(variant) {
        this.submitBtn.disabled = !variant.available;
        this.submitLabel.textContent = variant.available
          ? this.submitLabel.dataset.addToCartText || this.submitLabel.textContent
          : this.submitLabel.dataset.soldOutText || this.submitLabel.textContent;
      }

      bindSubmit() {
        this.submitBtn.addEventListener('click', () => {
          if (!this.mainSubmitButton || this.mainSubmitButton.disabled) return;
          this.mainSubmitButton.click();
        });
      }

      bindVisibility() {
        const observer = new IntersectionObserver(
          ([entry]) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('tdm-sticky-bar--visible', scrolledPast);
          },
          { threshold: 0 }
        );
        observer.observe(this.mainSubmitButton);
      }
    }
  );
}
